import {useCallback, useEffect, useMemo, useRef, useState} from 'react'

import {VoiceCallStatus} from './useVoiceCallState'
import {
  ANIMATION_DURATIONS,
  VoiceAnimationState,
  prefersReducedMotion,
} from './voiceAnimationConstants'

export interface VoiceAnimationStateProps {
  status: VoiceCallStatus
  isAiSpeaking: boolean
  isAwaitingResponse: boolean
  isMuted: boolean
  micLevel?: number
}

export const useVoiceAnimationState = (props: VoiceAnimationStateProps) => {
  const {status, isAiSpeaking, isAwaitingResponse, isMuted, micLevel = 0} = props

  const [animationState, setAnimationState] = useState<VoiceAnimationState>(
    VoiceAnimationState.IDLE
  )
  const [isEntering, setIsEntering] = useState(false)
  const [isExiting, setIsExiting] = useState(false)
  const previousStateRef = useRef<VoiceAnimationState>(VoiceAnimationState.IDLE)
  const timeoutIdsRef = useRef<Set<NodeJS.Timeout>>(new Set())
  const reducedMotion = useMemo(() => prefersReducedMotion(), [])

  const determineAnimationState = useCallback((): VoiceAnimationState => {
    if (isMuted) return VoiceAnimationState.MUTED
    if (status === 'ended') return VoiceAnimationState.IDLE
    if (isAiSpeaking) return VoiceAnimationState.SPEAKING
    if (isAwaitingResponse) return VoiceAnimationState.THINKING
    if (status === 'listening') return VoiceAnimationState.LISTENING
    return VoiceAnimationState.IDLE
  }, [status, isAiSpeaking, isAwaitingResponse, isMuted])

  useEffect(() => {
    const targetState = determineAnimationState()

    if (targetState === animationState) {
      return
    }

    timeoutIdsRef.current.forEach((id) => clearTimeout(id))
    timeoutIdsRef.current.clear()

    if (
      reducedMotion &&
      targetState !== VoiceAnimationState.MUTED &&
      targetState !== VoiceAnimationState.IDLE
    ) {
      setAnimationState(targetState)
      return
    }

    setIsExiting(true)

    const exitDuration =
      animationState === VoiceAnimationState.LISTENING
        ? ANIMATION_DURATIONS.LISTENING_EXIT
        : animationState === VoiceAnimationState.SPEAKING
          ? ANIMATION_DURATIONS.SPEAKING_EXIT
          : ANIMATION_DURATIONS.STATE_TRANSITION

    const exitTimeout = setTimeout(() => {
      previousStateRef.current = animationState
      setAnimationState(targetState)
      setIsExiting(false)
      setIsEntering(true)

      const enterDuration =
        targetState === VoiceAnimationState.LISTENING
          ? ANIMATION_DURATIONS.LISTENING_ENTRY
          : targetState === VoiceAnimationState.THINKING
            ? ANIMATION_DURATIONS.THINKING_ENTRY
            : targetState === VoiceAnimationState.SPEAKING
              ? ANIMATION_DURATIONS.SPEAKING_ENTRY
              : ANIMATION_DURATIONS.STATE_TRANSITION

      const enterTimeout = setTimeout(() => {
        setIsEntering(false)
      }, enterDuration)

      timeoutIdsRef.current.add(enterTimeout)
    }, exitDuration)

    timeoutIdsRef.current.add(exitTimeout)

    return () => {
      clearTimeout(exitTimeout)
    }
  }, [animationState, determineAnimationState, reducedMotion])

  useEffect(() => {
    if (status === 'ended' && animationState !== VoiceAnimationState.IDLE) {
      setAnimationState(VoiceAnimationState.IDLE)
      setIsEntering(false)
      setIsExiting(false)
    }
  }, [status, animationState])

  useEffect(() => {
    const timeoutIds = timeoutIdsRef.current
    return () => {
      timeoutIds.forEach((id) => clearTimeout(id))
      timeoutIds.clear()
    }
  }, [])

  const triggerReplay = useCallback(() => {
    if (animationState === VoiceAnimationState.SPEAKING) {
      setIsExiting(true)
      const replayTimeout = setTimeout(() => {
        setIsExiting(false)
        setIsEntering(true)
        const enterTimeout = setTimeout(() => {
          setIsEntering(false)
        }, ANIMATION_DURATIONS.REPLAY_RESTART)
        timeoutIdsRef.current.add(enterTimeout)
      }, ANIMATION_DURATIONS.REPLAY_RESTART)
      timeoutIdsRef.current.add(replayTimeout)
    }
  }, [animationState])

  return {
    animationState,
    isEntering: !reducedMotion && isEntering,
    isExiting: !reducedMotion && isExiting,
    triggerReplay,
    isListening: animationState === VoiceAnimationState.LISTENING,
    isThinking: animationState === VoiceAnimationState.THINKING,
    isSpeaking: animationState === VoiceAnimationState.SPEAKING,
    isMuted: animationState === VoiceAnimationState.MUTED,
    isIdle: animationState === VoiceAnimationState.IDLE,
    micLevel,
  }
}

export default useVoiceAnimationState
